import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Youtube, MessageCircle } from 'lucide-react';
import { Tile } from '@/components/bento';

const footerLinks = {
  company: [
    { label: 'About Us', href: '/About' },
    { label: 'Academy', to: '/Academy' },
    { label: 'Our Team', href: '/Team' },
    { label: 'Portfolio', href: '/Portfolio' },
    { label: 'Services', to: '/Services' },
    { label: 'Custom Builds', to: '/CustomBuilds' },
    { label: 'The Verdict', to: '/Verdict' },
    { label: 'Templates', to: '/Templates' },
    { label: 'Changelog', to: '/Changelog' },
    { label: 'System Status', to: '/Status' },
    { label: 'Partnership', to: '/Partnership' },
    { label: 'Affiliate Programme', to: '/Affiliate' },
  ],
  support: [
    { label: 'Contact', href: '/Contact' },
    { label: 'FAQ', href: '/FAQs' },
    { label: 'Privacy Policy', to: '/PrivacyPolicy' },
    { label: 'Terms of Use', to: '/TermsOfUse' },
    { label: 'Refund Policy', to: '/RefundPolicy' },
    { label: 'Affiliate T&C', to: '/AffiliateTnC' },
    { label: 'Acceptable Use', to: '/AcceptableUsePolicy' },
    { label: 'Data & Compliance', to: '/DataCompliance' },
  ],
};

const socials = [
  { icon: Instagram, href: 'https://instagram.com/builderslabofficial', label: 'Instagram' },
  { icon: Facebook, href: 'https://facebook.com/builderslabofficial', label: 'Facebook' },
  { icon: Twitter, href: 'https://twitter.com/builderslabofficial', label: 'X (Twitter)' },
  { icon: Youtube, href: 'https://youtube.com/builderslabofficial', label: 'YouTube' },
  { icon: MessageCircle, href: 'https://discord.gg/builderslabofficial', label: 'Discord' },
];

const linkCls = 'text-zinc-400 hover:text-white transition-colors text-[15px]';

function FooterLink({ link }) {
  return link.to
    ? <Link to={link.to} className={linkCls}>{link.label}</Link>
    : <a href={link.href} className={linkCls}>{link.label}</a>;
}

export default function Footer() {
  return (
    <footer className="bg-zinc-950 pt-6 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand */}
          <Tile className="md:col-span-2 lg:col-span-5 p-6 sm:p-8 flex flex-col">
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="inline-flex items-center gap-3 self-start group">
              <img src="/images/builderslab-logo.png" alt="BuildersLab Logo" className="h-9 w-auto object-contain group-hover:scale-105 transition-transform" />
              <span className="text-white text-2xl font-extrabold tracking-tight">BuildersLab</span>
            </Link>
            <p className="text-zinc-400 text-base leading-relaxed mt-5 max-w-md">
              BuildersLab is a vibe coding platform that helps you build websites and mobile apps using AI.
            </p>
            <div className="flex items-center gap-2.5 mt-8 lg:mt-auto pt-2">
              {socials.map(({ icon: Icon, href, label }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-11 h-11 rounded-2xl bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </Tile>

          {/* Company */}
          <Tile className="lg:col-span-4 p-6 sm:p-8">
            <h4 className="font-extrabold text-white mb-5 text-sm uppercase tracking-wider">Company</h4>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}><FooterLink link={link} /></li>
              ))}
            </ul>
          </Tile>

          {/* Legal & Support */}
          <Tile className="lg:col-span-3 p-6 sm:p-8">
            <h4 className="font-extrabold text-white mb-5 text-sm uppercase tracking-wider">Legal & Support</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}><FooterLink link={link} /></li>
              ))}
            </ul>
          </Tile>
        </div>

        {/* Bottom bar */}
        <div className="mt-6 px-1 flex flex-col md:flex-row justify-between items-center gap-4">
          <span className="text-zinc-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} BuildersLab Limited · RC 9689278. All rights reserved.
          </span>

          <div className="flex items-center gap-5 md:gap-6">
            <a
              href="mailto:info@builderslab.net"
              className="text-zinc-400 hover:text-orange-400 text-sm font-bold transition-colors"
            >
              Support Us / Donate
            </a>
            <a
              href="https://builderslab.net"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-semibold transition-colors border border-zinc-800 bg-zinc-900 rounded-full px-4 py-1.5 hover:bg-zinc-800"
            >
              System Status
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
